import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { environments } from '@brand/config/environments';

import { UserState } from '../../features/account/services/user.state';
import { AuthState } from '../../features/auth/services/auth.state';
import type { User } from '../models/api/user.model';
import { Platform } from './platform';

interface CrmPlugin {
  (method: string, ...args: unknown[]): void;
  q?: unknown[][];
}

interface CrmPluginData {
  id: string;
  hash: string;
  locale: string;
  params?: unknown[];
  setMeta(params: CrmPluginMeta): void;
}

interface CrmPluginMeta {
  contact: {
    name: string;
    custom_fields: {
      id: number;
      values: {
        value: string;
        enum: number;
      }[];
    }[];
  };
  lead: {
    name: string;
  };
}

interface CrmPluginConfig {
  onlinechat?: {
    user_id?: string;
  };
}

interface KommoBusMessage {
  method?: string;
  count?: number;
}

declare global {
  interface Window {
    crmPlugin?: CrmPlugin;
    crm_plugin?: CrmPluginData;
    crmPluginConfig?: CrmPluginConfig;
  }
}

@Injectable({ providedIn: 'root' })
export class KommoChat {
  private readonly authState = inject(AuthState);
  private readonly document = inject(DOCUMENT);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private readonly userState = inject(UserState);
  private readonly config = environments.KOMMO_CHAT;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );
  private readonly started = signal(false);
  private readonly openOnInit = signal(false);
  private initialized = false;
  private activeUserId: string | undefined;
  private hasAutoOpened = false;
  private openScheduled = false;
  private messageSoundBound = false;
  private notificationReady = false;
  private warmupTimer: number | null = null;
  private lastBadgeCount = 0;
  private lastSoundAt = 0;
  private notificationAudio: HTMLAudioElement | null = null;
  private audioUnlocked = false;

  private unlockInProgress = false;

  /**
   * El navegador bloquea el audio hasta que el usuario interactúa con la página.
   * Se reintenta en cada interacción hasta que un play silencioso funcione.
   */
  private readonly unlockAudio = (): void => {
    if (this.audioUnlocked || this.unlockInProgress) return;
    this.unlockInProgress = true;
    const audio = this.getNotificationAudio();
    audio.muted = true;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        this.audioUnlocked = true;
        this.toggleUnlockListeners(false);
      })
      .catch(() => undefined)
      .finally(() => {
        audio.muted = false;
        this.unlockInProgress = false;
      });
  };

  private toggleUnlockListeners(enabled: boolean): void {
    const view = this.document.defaultView;
    if (!view) return;
    for (const type of ['pointerdown', 'touchstart', 'keydown', 'click']) {
      if (enabled) {
        view.addEventListener(type, this.unlockAudio, { capture: true });
      } else {
        view.removeEventListener(type, this.unlockAudio, { capture: true });
      }
    }
  }

  private readonly onKommoBusMessage = (event: MessageEvent): void => {
    let data: KommoBusMessage | null = null;
    try {
      data =
        typeof event.data === 'string'
          ? (JSON.parse(event.data) as KommoBusMessage)
          : (event.data as KommoBusMessage);
    } catch {
      return;
    }
    if (!data || typeof data !== 'object' || data.method !== 'badge') return;

    // El iframe reenvía el badge (y un `bubble`) en cada update del socket,
    // incluido "el agente está escribiendo"; solo un aumento del contador de
    // no leídos significa un mensaje entrante nuevo, con el chat abierto o cerrado.
    const count = Number(data.count) || 0;
    if (this.notificationReady && count > this.lastBadgeCount) {
      this.playMessageSound();
    }
    this.lastBadgeCount = count;
  };

  constructor() {
    effect(() => {
      if (!this.started() || !this.platform.isBrowser || !this.config) return;
      if (this.isQuizRoute(this.currentUrl())) {
        this.destroy();
        return;
      }

      const isLoggedIn = this.authState.isLoggedIn();
      const user = this.userState.user();

      if (isLoggedIn && !user) {
        this.destroy();
        return;
      }

      const open =
        this.openOnInit() || this.hasRunChatShowParam(this.currentUrl());
      if (!open) {
        this.hasAutoOpened = false;
        this.openScheduled = false;
      }

      this.initialize(isLoggedIn ? user : null, open);
    });
  }

  /** @param open Si es true, abre el chat al inicializar (como si el usuario lo hubiera abierto). */
  start(open = false): void {
    this.openOnInit.set(open);
    this.started.set(true);
  }

  private hasRunChatShowParam(url: string): boolean {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) return false;
    const query = url.slice(queryIndex + 1).split('#')[0];
    return new URLSearchParams(query).get('runChatShow') === 'true';
  }

  private isQuizRoute(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return /^\/exams\/[^/]+\/attempts\/[^/]+\/?$/.test(path);
  }

  private initialize(user: User | null, open: boolean): void {
    const userId = user?.kommo_user_id;
    if (this.initialized && this.activeUserId === userId) {
      if (user) this.setUserMetadata(user);
      if (open) this.scheduleOpenChat();
      return;
    }
    if (this.initialized) this.destroy();

    const config = this.config;
    if (!config) return;

    if (userId) {
      window.crmPluginConfig = {
        onlinechat: { user_id: userId },
      };
    } else {
      delete window.crmPluginConfig;
    }

    window.crm_plugin = {
      id: config.id,
      hash: config.hash,
      locale: config.locale,
      setMeta(params: CrmPluginMeta) {
        this.params = (this.params ?? []).concat([params]);
      },
    };
    if (user) this.setUserMetadata(user);

    const crmPlugin: CrmPlugin = (method, ...args) => {
      crmPlugin.q = crmPlugin.q ?? [];
      crmPlugin.q.push([method, ...args]);
    };
    window.crmPlugin = crmPlugin;

    this.bindMessageSound(crmPlugin);

    if (open) this.scheduleOpenChat();

    const script = this.document.createElement('script');
    script.async = true;
    script.id = 'crm_plugin_script';
    script.src = 'https://gso.kommo.com/js/button.js';
    this.document.head.appendChild(script);

    this.activeUserId = userId;
    this.initialized = true;
  }

  private bindMessageSound(crmPlugin: CrmPlugin): void {
    if (this.messageSoundBound) return;
    this.messageSoundBound = true;

    // Precarga el mp3 (en este proyecto `public/` se sirve bajo /shared).
    this.getNotificationAudio();

    if (!this.audioUnlocked) this.toggleUnlockListeners(true);
    this.document.defaultView?.addEventListener('message', this.onKommoBusMessage);

    crmPlugin('onChatReady', () => {
      this.beginNotificationWarmup();
    });
  }

  /** Los badges de los primeros segundos (no leídos previos, saludo) solo fijan la línea base. */
  private beginNotificationWarmup(): void {
    this.notificationReady = false;
    if (this.warmupTimer != null) {
      window.clearTimeout(this.warmupTimer);
    }
    this.warmupTimer = window.setTimeout(() => {
      this.notificationReady = true;
      this.warmupTimer = null;
    }, 4000);
  }

  private getNotificationAudio(): HTMLAudioElement {
    if (!this.notificationAudio) {
      const src =
        this.config?.notificationSound ?? '/shared/sounds/notification.mp3';
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.volume = 1;
      this.notificationAudio = audio;
    }
    return this.notificationAudio;
  }

  private playMessageSound(): void {
    const nowMs = Date.now();
    if (nowMs - this.lastSoundAt < 800) return;
    this.lastSoundAt = nowMs;

    try {
      const base = this.getNotificationAudio();
      // cloneNode evita cortes si llegan varios mensajes seguidos.
      const clip = base.cloneNode(true) as HTMLAudioElement;
      clip.volume = 1;
      void clip.play().catch(() => {
        base.currentTime = 0;
        void base.play().catch(() => undefined);
      });
    } catch {
      // Autplay bloqueado o audio no soportado.
    }
  }

  /**
   * runChatShow demasiado pronto solo cambia el botón: el iframe aún no está
   * inited/renderizado. Esperamos onChatRenderDone (o onChatReady + delay).
   */
  private scheduleOpenChat(): void {
    if (this.hasAutoOpened || this.openScheduled || !window.crmPlugin) return;
    this.openScheduled = true;

    const open = () => this.openChatPanel();
    const alreadyReady = this.initialized;

    window.crmPlugin('onChatShow', () => {
      this.hasAutoOpened = true;
    });

    window.crmPlugin('onChatRenderDone', () => {
      open();
    });

    window.crmPlugin('onChatReady', () => {
      // Fallback: el iframe puede estar ready antes del primer render.
      window.setTimeout(open, 400);
      window.setTimeout(open, 1000);
    });

    // Si el widget ya estaba montado, onChatReady/RenderDone no se re-disparan.
    if (alreadyReady) {
      window.setTimeout(open, 0);
      window.setTimeout(open, 400);
    }
  }

  private openChatPanel(): void {
    if (this.hasAutoOpened || !window.crmPlugin) return;
    window.crmPlugin('runChatShow');

    // Si el panel sigue oculto, forzar clases del livechat (API a veces deja solo el botón).
    window.setTimeout(() => {
      if (this.hasAutoOpened) return;
      const panel = this.document.querySelector(
        '.amo-livechat_chat, [class*="amo-livechat-iframe"]'
      ) as HTMLElement | null;
      if (!panel) {
        window.crmPlugin?.('runChatShow');
        return;
      }
      panel.classList.remove('amo-livechat_hidden', 'amo-livechat_opening');
      panel.classList.add('amo-livechat_chat');
      panel.style.visibility = 'visible';
      panel.style.opacity = '1';
      panel.style.pointerEvents = 'auto';
    }, 300);
  }

  private setUserMetadata(user: User): void {
    const fullName = `${user.usuario_nombres} ${user.usuario_apellidos}`.trim();
    const customFields: CrmPluginMeta['contact']['custom_fields'] = [];

    if (user.usuario_telefono) {
      customFields.push({
        id: 175218,
        values: [{ value: user.usuario_telefono, enum: 136290 }],
      });
    }
    if (user.usuario_correo) {
      customFields.push({
        id: 175220,
        values: [{ value: user.usuario_correo, enum: 136300 }],
      });
    }

    window.crm_plugin?.setMeta({
      contact: {
        name: fullName,
        custom_fields: customFields,
      },
      lead: {
        name: fullName,
      },
    });
  }

  private destroy(): void {
    if (!this.initialized) return;

    this.toggleUnlockListeners(false);
    this.document.defaultView?.removeEventListener('message', this.onKommoBusMessage);

    window.crmPlugin?.('runDestroy');
    this.document.getElementById('crm_plugin_script')?.remove();
    delete window.crmPlugin;
    delete window.crm_plugin;
    delete window.crmPluginConfig;
    this.activeUserId = undefined;
    this.initialized = false;
    this.hasAutoOpened = false;
    this.openScheduled = false;
    this.messageSoundBound = false;
    this.notificationReady = false;
    this.audioUnlocked = false;
    this.unlockInProgress = false;
    this.lastBadgeCount = 0;
    if (this.warmupTimer != null) {
      window.clearTimeout(this.warmupTimer);
      this.warmupTimer = null;
    }
    if (this.notificationAudio) {
      this.notificationAudio.pause();
      this.notificationAudio.src = '';
      this.notificationAudio = null;
    }
  }
}
